/**
 * №53 · 53.0.1 (D-CR2, D-CR25) — THE STRIP FRAME.
 *
 * Crew's `.strip` anatomy (`docs/53-mockups/crew.css:37-47`): sticky, three-column
 * grid (`auto 1fr auto`), a dial + a headline figure on the left, the key-pair
 * counters on the right. **Day one has no dial and no headline figure** — D-CR2 is
 * absolute: a tile renders only when its number is real, and the billable/handled
 * arithmetic has no honest source until 53.5. So this rung's strip renders the
 * ONE thing crew's leftmost slot CAN honestly carry today — the clock, in the same
 * vocabulary `ShellNav`'s rail-foot `.shellclock` already draws it — and the three
 * counters that ARE real: need-you, blocked, hires. No approved drawing exists for
 * this exact degraded composition (every mockup in `docs/53-mockups/` and
 * `docs/53-entity-layout-options/vision.html` draws the FULL strip, dial and all —
 * D-CR26 is a later rung); this is this rung's own stated ASSUME, flagged in
 * findings, built from crew.css's own vocabulary rather than invented from taste.
 *
 * The dial component itself (crew's 64px donut) is built as `StripDial.tsx` but is
 * never imported here — "built but unmounted", exactly as the spec requires. 53.5
 * mounts it once utilisation has an honest source.
 *
 * Existing hooks only, no new server calls (D-CR9/53 scope):
 *   need you → `useNeedsYouGlanceCount()` — the SAME number the rail's "Needs you"
 *               row badges, extracted there for exactly this reuse (RC1-3).
 *   blocked  → `useWorkforceStatus()`, the same derivation `WorkforceBoardPage`
 *               already uses for its "hires blocked" tile.
 *   hires    → the roster, read the same way the shell and the Workforce board
 *               both already read it (`library_root`, agents minus the root node).
 *
 * Mounted once in `AppShellLayout`, a sibling of `RecordTabs` (`AppShellLayout.tsx`)
 * — a shell element, not a per-page one. It renders on every `/app` route.
 */
import { useMemo } from "react";
import { useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { useWorkforceStatus } from "../../hooks/use-workforce-status";
import { useNeedsYouGlanceCount } from "../../hooks/use-needs-you-count";

export function AppStrip() {
  const wsInfo = useWorkspaceInfo();
  // Same read as `WorkforceBoardPage` — library-root agents, minus the workspace's
  // own root node (which is not a hire).
  const roster = useMemo(
    () => (wsInfo.data?.library_root ?? [])
      .filter((n: { type: string; is_root?: boolean }) => n.type === "agent" && !n.is_root)
      .map((n: { id: string; name: string }) => ({ id: n.id, name: n.name })),
    [wsInfo.data?.library_root],
  );
  const ids = useMemo(() => roster.map((r) => r.id), [roster]);
  const status = useWorkforceStatus(ids, ids.length > 0);
  const blockedCount = useMemo(
    () => Object.values(status.data ?? {}).filter((s) => s.blocked).length,
    [status.data],
  );
  const needsYouCount = useNeedsYouGlanceCount();
  const hiresCount = roster.length;

  return (
    <div className="strip" data-testid="app-strip">
      {/* crew's leftmost slot (the dial, absent today) — the rail-foot clock's own
          vocabulary, not a new one. Not a ticking timer: same reasoning as
          `ShellNav`'s `.shellclock` — a live second hand here is motion with
          nothing behind it. */}
      <div className="strip__clock" data-testid="strip-clock">
        <b>{new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}</b>
        <span>{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</span>
      </div>
      <div className="strip__metrics" data-testid="strip-metrics">
        <div className="k k--warn" data-testid="strip-needs-you">
          <b>{needsYouCount}</b><span>need you</span>
        </div>
        <div className="k" data-testid="strip-blocked">
          <b>{blockedCount}</b><span>blocked</span>
        </div>
        <div className="k" data-testid="strip-hires">
          <b>{hiresCount}</b><span>hires</span>
        </div>
      </div>
    </div>
  );
}
